// src/components/dashboard/StarterKitDialog.tsx
// NEW — closes the Discovery/Selection gap from the Starter Kit user
// story. Conversational-weight overlay (Dialog, not a page/gallery route)
// since there's currently exactly one kit — a full gallery page would be
// over-built for a one-item list. Structured so a Carousel of kit cards
// slots in later without restructuring: KIT_CARDS is already an array,
// just rendered as a single-item stack for now.
//
// Calls POST /outcomes/install-kit (installStarterKit,
// functions/fdwApi/tools/install-starter-kit.ts). Path updated to the
// /api-prefixed route during the dashboard rewrite migration — this
// note is unrelated to and does not resolve the separate backend-wiring
// caveat below.
//
// STILL NOT YET APPLIED to the backend as of this file's last edit —
// this dialog will render and open correctly, but the "Install this
// kit" button will fail (404 or equivalent) until registry.ts,
// install-starter-kit.ts, and the _toolTable.ts / _toolRegistry.ts /
// dashboard.ts wiring are actually added to the repo. Do not treat this
// file's presence, or the path-prefix fix, as evidence the feature
// works end-to-end — those are two independent issues.
//
// On success, closes and navigates to the first created outcome's detail
// page — this is the "Review" step (journey step 4) landing point. If
// more than one outcome was created, this still only opens the first —
// a real kit summary screen (multi-outcome review) is a known, separate
// gap, not solved here.
//
// Dialog/DialogContent/DialogHeader/DialogTitle/DialogDescription/
// DialogFooter — confirmed real, Base UI-backed (@base-ui/react/dialog),
// no asChild risk since none of this file's usage relies on that prop.
//
// MIGRATED — local FDW_BASE / dashboardToken / getDashboardToken /
// authedFetch block removed in favor of the shared dashboardApi client.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sparkles, Loader2Icon } from 'lucide-react';
import { authedFetch } from '../../services/dashboardApi';

// ── Static kit catalog for display purposes only. Mirrors
// starterKits/registry.ts's KIT_REGISTRY entries — kept as a small,
// separate frontend list rather than fetching the registry over the
// network, since there's no GET endpoint for "list available kits" yet
// (install-starter-kit.ts's tool takes a kit_id directly, it doesn't
// expose a browse/list operation). If a second kit ships, add it here
// AND in the backend registry — these two lists are not currently kept
// in sync automatically.
interface KitCard {
  kitId: string;
  name: string;
  vertical: string;
  description: string;
  creates: string[];
}

const KIT_CARDS: KitCard[] = [
  {
    kitId: 'transactional-legal-nda',
    name: 'Standard NDA Review',
    vertical: 'Transactional Legal',
    description: 'A pre-built package for reviewing standard mutual NDAs, with default qualification and escalation rules already attached.',
    creates: [
      '1 package — Standard Mutual NDA Review',
      '3 rules — qualification, escalation, scope limit',
      'Default work mode: Ask First',
    ],
  },
];

interface InstallResult {
  success: boolean;
  kitId: string;
  created: {
    outcomes: Array<{ id: string; name: string; status: string }>;
    rules: Array<{ id: string; scope: string; outcomeId: string }>;
    offerings: Array<{ id: string; name: string }>;
  };
  message?: string;
  errors?: string[];
}

interface StarterKitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const StarterKitDialog: React.FC<StarterKitDialogProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [installingKitId, setInstallingKitId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleInstall = async (kitId: string) => {
    setInstallingKitId(kitId);
    setError(null);
    try {
      const res = await authedFetch('/outcomes/install-kit', {
        method: 'POST',
        body: JSON.stringify({ kitId, pauseAfterInstall: true }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to install this kit.');
      }
      const data: InstallResult = await res.json();
      if (!data.success && data.errors?.length) {
        // Partial install — surface it rather than silently navigating
        // away as if everything worked.
        setError(data.errors.join(' '));
        return;
      }

      onOpenChange(false);
      const firstOutcome = data.created.outcomes[0];
      if (firstOutcome) {
        navigate(`/dashboard/outcomes/${firstOutcome.id}`);
      } else {
        navigate('/dashboard/outcomes');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong installing this kit.');
    } finally {
      setInstallingKitId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-brand-accent" />
            Start with a kit
          </DialogTitle>
          <DialogDescription>
            A kit creates a ready-made work package with default rules already attached. Everything lands in draft — nothing goes live until you publish it yourself.
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="space-y-3 py-2">
          {KIT_CARDS.map((kit) => (
            <div
              key={kit.kitId}
              className="rounded-xl border border-brand-border bg-brand-white p-4"
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <Badge className="bg-brand-surface text-brand-gray mb-2">{kit.vertical}</Badge>
                  <h3 className="text-sm font-medium text-brand-black">{kit.name}</h3>
                </div>
              </div>
              <p className="text-sm text-brand-charcoal font-light leading-relaxed mb-3">
                {kit.description}
              </p>
              <ul className="space-y-1 mb-4">
                {kit.creates.map((line, i) => (
                  <li key={i} className="text-xs text-brand-gray font-mono flex items-start gap-1.5">
                    <span className="text-brand-accent shrink-0">·</span>
                    {line}
                  </li>
                ))}
              </ul>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleInstall(kit.kitId)}
                disabled={installingKitId === kit.kitId}
                className="w-full"
              >
                {installingKitId === kit.kitId ? (
                  <>
                    <Loader2Icon className="w-4 h-4 animate-spin mr-1.5" />
                    Setting up…
                  </>
                ) : (
                  'Use this kit'
                )}
              </Button>
            </div>
          ))}
        </div>

        <DialogFooter>
          <p className="text-xs text-brand-gray font-light">
            You'll land on the new package to review before anything is visible to agents.
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};